import React, { useEffect, useState } from 'react';
import {
  isBannerSlideLoaded,
  preloadBannerSlides,
} from '../utils/bannerSlides';

const VARIANT_STYLES = {
  login: {
    container:
      'relative aspect-[16/10] w-full overflow-hidden rounded-2xl bg-white md:aspect-auto md:h-full md:min-h-[18rem]',
    image: 'absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-500 ease-in-out',
    dots: 'absolute bottom-3 right-4 z-20 flex gap-2 md:bottom-4 md:right-6',
    dotActive: 'bg-brand',
    dotInactive: 'bg-gray-300 hover:bg-gray-400',
    naturalHeight: false,
  },
  landing: {
    container: 'relative w-full overflow-hidden bg-[#141414]',
    image:
      'absolute inset-x-0 top-0 block h-auto w-full transition-opacity duration-500 ease-in-out',
    sizer: 'invisible block h-auto w-full pointer-events-none select-none',
    dots: 'absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-2 sm:bottom-5',
    dotActive: 'bg-white',
    dotInactive: 'bg-transparent border border-white/80',
    naturalHeight: true,
  },
};

function BannerSlider({
  slides,
  currentSlide,
  onSelectSlide,
  onFirstSlideReady,
  variant = 'landing',
  scope = 'default',
  alt = 'Banner',
}) {
  const [loadedSlides, setLoadedSlides] = useState(
    () => new Set(slides.filter(isBannerSlideLoaded))
  );
  const [displayIndex, setDisplayIndex] = useState(0);
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.landing;
  const slidesKey = `${scope}:${slides.join('\0')}`;

  const slideCount = slides.length;
  const safeIndex = slideCount ? currentSlide % slideCount : 0;
  const firstSlideReady = Boolean(slides[0] && loadedSlides.has(slides[0]));

  const markLoaded = (urls) => {
    setLoadedSlides((prev) => {
      const next = new Set(prev);
      let changed = false;
      urls.forEach((src) => {
        if (src && !next.has(src)) {
          next.add(src);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoadedSlides(new Set(slides.filter(isBannerSlideLoaded)));
    setDisplayIndex(0);

    if (!slides.length) {
      onFirstSlideReady?.(false);
      return undefined;
    }

    onFirstSlideReady?.(false);

    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = slides[0];
    if ('fetchPriority' in preloadLink) preloadLink.fetchPriority = 'high';
    document.head.appendChild(preloadLink);

    preloadBannerSlides([slides[0]]).then(() => {
      if (cancelled) return;
      markLoaded([slides[0]]);
      onFirstSlideReady?.(true);
    });

    return () => {
      cancelled = true;
      preloadLink.remove();
    };
  }, [slidesKey, slides, onFirstSlideReady]);

  useEffect(() => {
    if (!firstSlideReady || slides.length <= 1) return undefined;

    const rest = slides.filter((src) => src && !isBannerSlideLoaded(src) && !loadedSlides.has(src));
    if (!rest.length) return undefined;

    let cancelled = false;
    preloadBannerSlides(rest).then(() => {
      if (!cancelled) markLoaded(rest);
    });

    return () => {
      cancelled = true;
    };
  }, [firstSlideReady, slides, slidesKey, loadedSlides]);

  useEffect(() => {
    if (!slides.length) return undefined;

    const targetSrc = slides[safeIndex];
    if (targetSrc && loadedSlides.has(targetSrc)) {
      setDisplayIndex(safeIndex);
      return undefined;
    }

    const pending = [slides[safeIndex], slides[(safeIndex + 1) % slides.length]].filter(
      (src, index, list) => src && list.indexOf(src) === index && !loadedSlides.has(src)
    );

    if (!pending.length) return undefined;

    let cancelled = false;
    preloadBannerSlides(pending).then(() => {
      if (cancelled) return;
      markLoaded(pending);
    });

    return () => {
      cancelled = true;
    };
  }, [safeIndex, slides, slidesKey, loadedSlides]);

  return (
    <div className={styles.container}>
      {styles.naturalHeight && firstSlideReady && slides[0] ? (
        <img src={slides[0]} alt="" aria-hidden className={styles.sizer} />
      ) : null}

      {slides.map((src, index) => {
        if (!loadedSlides.has(src)) return null;

        const isActive = index === displayIndex;
        return (
          <img
            key={src}
            src={src}
            alt={isActive ? alt : ''}
            aria-hidden={!isActive}
            className={`${styles.image} ${
              isActive ? 'z-10 opacity-100' : 'z-0 opacity-0'
            }`}
            decoding="async"
            fetchPriority={index === 0 ? 'high' : 'auto'}
          />
        );
      })}

      {slideCount > 1 ? (
        <div className={styles.dots}>
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              disabled={!firstSlideReady}
              className={`h-2 w-2 rounded-full transition-colors ${
                index === displayIndex ? styles.dotActive : styles.dotInactive
              } ${!firstSlideReady ? 'cursor-wait opacity-50' : ''}`}
              onClick={() => {
                if (!firstSlideReady) return;
                onSelectSlide(index);
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default BannerSlider;
